import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { ConfirmationModel } from "../../components/features/common";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../components/UI/Declarations";
import { PagedList } from "../../components/UI/PagedList/PagedList";
import { formatEwcDisplayCode } from "../../components/Forms/EwcCodeInput";
import { ewcCodesService } from "../../services/reference/ewcCodes.service";
import {
    addEntityActionLabel,
    appRoutes,
    buildRecordLifecycleConfirmProps,
    EntityLabels,
    formatReferenceSourceDisplay,
    ListPageTitles,
    recordLifecycleListConfirmBodies,
    referenceAdminEditPath,
    referenceAdminNewPath,
    resolveActionErrorMessage,
    showToastSuccess,
} from "../../utils";
import {
    buildEwcAdminPagedListActions,
    getEwcAdminActionSuccessMessage,
    type EwcAdminConfirmKind,
} from "./ewcAdminListActions";

type ConfirmKind = EwcAdminConfirmKind | null;

const EwcCodesListPage = () => {
    const navigate = useNavigate();

    const [refreshNumber, setRefreshNumber] = useState(0);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [confirmEwcId, setConfirmEwcId] = useState<string | null>(null);
    const [confirmEwcLabel, setConfirmEwcLabel] = useState("");

    const closeConfirm = () => {
        setConfirmKind(null);
        setConfirmEwcId(null);
        setConfirmEwcLabel("");
    };

    const runConfirmedAction = () => {
        if (!confirmEwcId || !confirmKind) return;
        const id = confirmEwcId;
        const op =
            confirmKind === "disable"
                ? ewcCodesService.disable(id)
                : confirmKind === "enable"
                  ? ewcCodesService.enable(id)
                  : confirmKind === "softDelete"
                    ? ewcCodesService.softDelete(id)
                    : ewcCodesService.restore(id);
        op.then(() => {
            showToastSuccess(getEwcAdminActionSuccessMessage(confirmKind, confirmEwcLabel));
            setActionError(null);
            setRefreshNumber((n) => n + 1);
            closeConfirm();
        }).catch((e: unknown) => {
            setActionError(resolveActionErrorMessage(e));
            closeConfirm();
        });
    };

    const openConfirm = (kind: ConfirmKind, item: { id: string; code: string }) => {
        setConfirmKind(kind);
        setConfirmEwcId(item.id);
        setConfirmEwcLabel(item.code);
    };

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.ewcCode,
                confirmEwcLabel,
                recordLifecycleListConfirmBodies(confirmEwcLabel)
            ),
        [confirmKind, confirmEwcLabel]
    );

    const columns: IColumn[] = useMemo(
        () => [
            {
                member: "code",
                renderAs: ColumnRender.customRenderer,
                title: "Code",
                width: 160,
                customRenderer: (item: { code?: string }) => (
                    <td className="text-start">
                        <span className="font-monospace">{formatEwcDisplayCode(item.code)}</span>
                    </td>
                ),
            } as IColumn,
            { member: "description", renderAs: ColumnRender.text, title: "Description", width: 280 } as IColumn,
            { member: "isHazardous", renderAs: ColumnRender.checkCross, title: "Hazardous", width: 100, align: "center" } as IColumn,
            {
                member: "source",
                renderAs: ColumnRender.customRenderer,
                title: "Source",
                width: 120,
                customRenderer: (item: { source?: string }) => (
                    <td className="text-start">{formatReferenceSourceDisplay(item.source)}</td>
                ),
            } as IColumn,
            { member: "isActive", renderAs: ColumnRender.checkCross, title: "Active", width: 90, align: "center" } as IColumn,
        ],
        []
    );

    const filters: IFilter[] = useMemo(
        () => [
            { title: "Code", member: "code", type: FilterType.text } as IFilter,
            { title: "Description", member: "description", type: FilterType.text } as IFilter,
            { title: "Source", member: "source", type: FilterType.text } as IFilter,
            {
                title: "Active",
                member: "isActive",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            {
                title: "Hazardous",
                member: "isHazardous",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            {
                title: "Deleted",
                member: "isDeleted",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
                defaultValue: false,
            } as IFilter,
        ],
        []
    );

    const itemActions: IPagedListAction[] = useMemo(
        () => [
            {
                text: "Edit",
                onClick: (item: { id: string }) => navigate(referenceAdminEditPath("ewcCodes", item.id)),
                primary: true,
            } as IPagedListAction,
            ...buildEwcAdminPagedListActions((kind, item) => openConfirm(kind, item)),
        ],
        [navigate]
    );

    const headActions: IPagedListAction[] = useMemo(
        () => [
            {
                text: addEntityActionLabel(EntityLabels.ewcCode),
                icon: faPlus,
                onClick: () => navigate(referenceAdminNewPath("ewcCodes")),
                primary: true,
            } as IPagedListAction,
        ],
        [navigate]
    );

    return (
        <Container fluid>
            <Helmet>
                <title>{ListPageTitles.ewcCodes}</title>
            </Helmet>
            <Row className="mb-3">
                <Col>
                    <ResponsiveBreadcrumb
                        items={[
                            { text: EntityLabels.referenceData, to: appRoutes.codes },
                            { text: ListPageTitles.ewcCodes },
                        ]}
                    />
                    <h1>{ListPageTitles.ewcCodes}</h1>
                    <p className="text-muted">
                        European Waste Catalogue codes for regulatory reporting. Administrators can add, edit, disable, or remove codes.
                    </p>
                    {actionError && (
                        <Alert variant="danger" className="mt-2" onClose={() => setActionError(null)} dismissible>
                            {actionError}
                        </Alert>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    <PagedList
                        columns={columns}
                        filters={filters}
                        headActions={headActions}
                        itemActions={itemActions}
                        rowActionsAriaLabel="EWC code actions"
                        showDeletedRowIndicator
                        defaultSortBy="Code"
                        defaultSortOrder={SortOrder.ascending}
                        getUrl="/api/referencedata/ewcs/all"
                        rowFormatter={() => null}
                        persistanceKey="EwcCodesListPage"
                        refreshNumber={refreshNumber}
                    />
                </Col>
            </Row>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runConfirmedAction();
                    else closeConfirm();
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { EwcCodesListPage };
